import { _js } from '@ifixit/utils/localize';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { cn } from '@ifixit/ui/lib/utils';
import { useState } from 'react';
import {
   bulletColor,
   bulletLabel,
   bulletSvg,
   COLOR_BULLETS,
   ICON_BULLETS,
   isIconBullet,
} from './bullets';

/**
 * The dot or icon for one bullet value, sized to sit in a line row. Icon
 * bullets render the same inline SVGs the published guide uses; the
 * reminder icon keeps its boxed outline from the guide styles.
 */
function BulletMark({ name, className }: { name: string; className?: string }) {
   if (isIconBullet(name)) {
      return (
         <span
            className={cn(
               'inline-flex size-4 items-center justify-center [&_svg]:size-full [&_svg]:fill-current',
               name === 'icon_reminder' && 'rounded-sm border-[1.5px] border-current p-0.5',
               className
            )}
            style={{ color: bulletColor(name) }}
            // Trusted inline SVGs from bullet_icons.js, not user content.
            dangerouslySetInnerHTML={{ __html: bulletSvg(name) }}
         />
      );
   }
   return (
      <span
         className={cn('inline-block size-3 rounded-full', className)}
         style={{ backgroundColor: bulletColor(name) }}
      />
   );
}

export function BulletSelector({
   value,
   onChange,
}: {
   value: string;
   onChange: (bullet: string) => void;
}) {
   const [open, setOpen] = useState(false);

   const select = (bullet: string) => {
      onChange(bullet);
      setOpen(false);
   };

   return (
      <Popover open={open} onOpenChange={setOpen}>
         <PopoverTrigger asChild>
            <button
               type="button"
               // The bullet is primarily a click target that opens the color
               // popover, so it shows a pointer cursor; it still drags the line
               // it sits on via data-line-drag-through.
               data-line-drag-through
               className="group/bullet relative flex size-7 shrink-0 cursor-pointer items-center justify-center rounded outline-none hover:bg-gray-100 focus-visible:ring-[3px] focus-visible:ring-ring/50"
               aria-label={_js('Change bullet')}
               title={_js('Change bullet')}
            >
               <BulletMark name={value} />
               {/* The corner caret is what says "this opens a menu" — shown
                   on hover/focus, and always where hover doesn't exist. */}
               <FaIcon
                  name="caret-down"
                  aria-hidden
                  className="absolute -bottom-0.5 right-0 size-2 text-gray-400 opacity-0 transition-opacity group-hover/bullet:opacity-100 group-focus-visible/bullet:opacity-100 pointer-coarse:opacity-100"
               />
            </button>
         </PopoverTrigger>
         <PopoverContent className="w-56 p-2" align="start">
            <div className="mb-1 grid grid-cols-8 gap-1">
               {COLOR_BULLETS.map(bullet => (
                  <button
                     key={bullet.name}
                     type="button"
                     className={cn(
                        'flex size-6 items-center justify-center rounded outline-none hover:bg-gray-100 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        bullet.name === value && 'bg-gray-100 ring-1 ring-gray-400'
                     )}
                     onClick={() => select(bullet.name)}
                     aria-label={bulletLabel(bullet.name)}
                     aria-pressed={bullet.name === value}
                     title={bulletLabel(bullet.name)}
                  >
                     <BulletMark name={bullet.name} />
                  </button>
               ))}
            </div>
            {/* The colors aren't decoration: a colored bullet pairs a line with
                the same-colored marker drawn on the step image. Say so —
                first-time authors have no other way to learn it. */}
            <p className="mb-2 text-xs text-gray-500">
               {_js('Match the bullet color to the corresponding markup on the image.')}
            </p>
            <div className="flex flex-col">
               {ICON_BULLETS.map(bullet => (
                  <button
                     key={bullet.name}
                     type="button"
                     className={cn(
                        'flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm outline-none hover:bg-gray-100 focus-visible:ring-[3px] focus-visible:ring-ring/50',
                        bullet.name === value && 'bg-gray-100'
                     )}
                     onClick={() => select(bullet.name)}
                     aria-pressed={bullet.name === value}
                  >
                     <BulletMark name={bullet.name} />
                     <span>{bulletLabel(bullet.name)}</span>
                  </button>
               ))}
            </div>
         </PopoverContent>
      </Popover>
   );
}
